import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ChevronRight, Check } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import MFASetup from '../Auth/MFASetup';

const Login = () => {
    console.log('[LOGIN] Component rendering - LATEST VERSION 2025-01-07 17:01');
    const mountCount = useRef(0);
    const navigate = useNavigate();
    const { isAuthenticated, user, mfaRequired, mfaSetupRequired, login, clearMFAState } = useContext(AuthContext);
    
    // Test if login function exists
    console.log('[LOGIN] Login function from context:', typeof login);
    console.log('[LOGIN] TESTING BUTTON CLICK HANDLER - this should appear immediately');
    
    // Check if user is already authenticated and redirect to home
    useEffect(() => {
        console.log('[LOGIN] Checking auth state for redirect:', {
            isAuthenticated,
            hasUser: !!user,
            userEmail: user?.email,
            mfaRequired,
            mfaSetupRequired,
            currentPath: window.location.pathname
        });

        if (isAuthenticated && user && !mfaRequired && !mfaSetupRequired) {
            console.log('[LOGIN] User fully authenticated, redirecting to home:', user.email);
            // User is fully authenticated (no MFA required), redirect to home
            navigate('/studies/');
        } else if (isAuthenticated && user && (mfaRequired || mfaSetupRequired)) {
            console.log('[LOGIN] User authenticated but MFA required, staying on login page:', {
                email: user.email,
                mfaRequired,
                mfaSetupRequired
            });
            // User is authenticated but needs MFA - stay on login page to show MFA prompt
            // The MFAPrompt component will handle showing the modal
        } else if (!isAuthenticated) {
            console.log('[LOGIN] User not authenticated, staying on login page');
            // Check local storage as fallback
            const userData = localStorage.getItem('user');
            if (userData) {
                try {
                    const localUser = JSON.parse(userData);
                    console.log('[LOGIN] User found in localStorage but not in context:', localUser.email);
                } catch (e) {
                    console.error('[LOGIN] Error parsing user data from localStorage:', e);
                    localStorage.removeItem('user');
                }
            }
        }
    }, [navigate, isAuthenticated, user, mfaRequired, mfaSetupRequired]);
    
    useEffect(() => {
        mountCount.current += 1;
        console.log('[LOGIN] Component mounted, mount count:', mountCount.current);
        return () => {
            console.log('[LOGIN] Component unmounting');
        };
    }, []);

    const [email, setEmail] = useState(() => {
        const remembered = localStorage.getItem('rememberedEmail');
        console.log('[LOGIN] Initial email state:', remembered || '');
        return remembered || '';
    });
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [networkError, setNetworkError] = useState(null);
    const [pendingApproval, setPendingApproval] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    // AuthContext and navigate are defined above

    // Clear error when inputs change
    useEffect(() => {
        if (error || pendingApproval) {
            console.log('[LOGIN] Clearing error state due to input change');
            setError(null);
            setPendingApproval(false);
        }
    }, [email, password]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('[LOGIN] Form submission started', {
            email: email,
            hasPassword: !!password,
            rememberMe: rememberMe,
            currentLoading: loading
        });

        if (loading) {
            console.log('[LOGIN] Already processing login, skipping');
            return;
        }

        setLoading(true);
        setError(null);
        setNetworkError(null);

        console.log('[LOGIN] Starting login process with credentials:', {
            email: email,
            passwordLength: password?.length || 0,
            rememberMe: rememberMe
        });
        try {
            // Clear remembered email if not checking "Remember Me"
            if (!rememberMe) {
                console.log('[LOGIN] Removing remembered email');
                localStorage.removeItem('rememberedEmail');
            }

            console.log('[LOGIN] About to call login function from AuthContext');
            const result = await login(email, password, rememberMe);
            console.log('[LOGIN] Login function completed, result:', result);
        } catch (error) {
            console.log('[LOGIN] Login error caught:', {
                error: error,
                message: error?.message,
                status: error?.status,
                response: error?.response,
                accountLocked: error?.accountLocked,
                passwordExpired: error?.passwordExpired,
                errorType: typeof error,
                errorKeys: Object.keys(error || {})
            });
            
            // Improved error handling with better safety checks
            if (error && typeof error === 'object') {
                if (error.accountLocked) {
                    console.log('[LOGIN] Setting account locked error');
                    setError(error.message || 'Account is locked'); 
                } else if (error.passwordExpired) {
                    console.log('[LOGIN] Setting password expired error');
                    setError('Your password is expired, please use the forgot password link above to reset your password');
                } else if (error.message === 'Invalid credentials' || error.message === 'Authentication failed') {
                    console.log('[LOGIN] Setting authentication error');
                    setError('Invalid email or password');
                } else if (error.response?.status === 401 || error.status === 401) {
                    console.log('[LOGIN] Setting 401 authentication error');
                    setError('Invalid email or password');
                } else if (error.response?.status === 429 || error.status === 429) {
                    console.log('[LOGIN] Setting rate limit error');
                    setError('Too many login attempts. Please wait a moment and try again.');
                } else if (error.response || error.status) {
                    // The server responded, just with an unexpected status — not a
                    // connectivity problem. Don't mislabel it as one.
                    console.log('[LOGIN] Setting unexpected server error', error.status || error.response?.status);
                    setError('Something went wrong during sign in. Please try again.');
                } else {
                    console.log('[LOGIN] Setting network/connection error');
                    setNetworkError('Unable to connect to the server. Please try again.');
                }
            } else {
                console.log('[LOGIN] Setting generic network error for unknown error type');
                setNetworkError('An unexpected error occurred. Please try again.');
            }
        } finally {
            console.log('[LOGIN] Completing login process');
            setLoading(false);
        }
    };

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            setEmail(value);
        } else if (name === 'password') {
            setPassword(value);
        }
    }, []);

    // MFA Setup handlers
    const handleMFASetupComplete = () => {
        console.log('[LOGIN] MFA setup completed successfully');
        // Redirect to home page after successful MFA setup
        navigate('/studies/');
    };

    const handleMFASetupCancel = () => {
        console.log('[LOGIN] MFA setup cancelled');
        // For now, just log - in production you might want to logout
        // or show a warning that MFA is required
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: { 
                type: "spring",
                stiffness: 100,
                damping: 10
            }
        }
    };

    return (
        <div className="min-h-screen flex flex-col justify-start pt-20 px-4 sm:px-6 lg:px-8">
            <motion.div 
                className="w-full max-w-md mx-auto"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl overflow-hidden shadow-xl dark:shadow-gray-900/20">
                    <div className="px-8 py-10">
                        <div className="text-center mb-6">
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Welcome Back</h2>
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Sign in to your account</p>
                        </div>
                        <form 
                            className="space-y-6" 
                            onSubmit={(e) => {
                                console.log('[LOGIN] Form onSubmit triggered! Preventing default.');
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('[LOGIN] About to call handleSubmit');
                                
                                // Ensure we don't call handleSubmit if already loading to prevent double submissions
                                if (loading) {
                                    console.log('[LOGIN] Already loading, skipping form submission');
                                    return false;
                                }
                                
                                // Call handleSubmit and catch any unhandled errors
                                try {
                                    handleSubmit(e);
                                } catch (formError) {
                                    console.error('[LOGIN] Error in form submission handler:', formError);
                                    setError('An unexpected error occurred. Please try again.');
                                    setLoading(false);
                                }
                                
                                return false; // Ensure no form submission
                            }}
                            onInput={() => console.log('[LOGIN] Form input detected')}
                            onChange={() => console.log('[LOGIN] Form change detected')}
                        >
                            {/* Email input */}
                            <div>
                                <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Email address
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        id="email-address"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={handleInputChange}
                                    />
                                    <Mail className="absolute left-3 top-2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                                </div>
                            </div>

                            {/* Password input */}
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    Password
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        autoComplete="current-password"
                                        required
                                        className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/20 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={handleInputChange}
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                        ) : (
                                            <Eye className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Remember me checkbox and Forgot password link */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center hidden">
                                    <label className="flex items-center cursor-pointer">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                                className="sr-only"
                                            />
                                            <div className={`w-5 h-5 border-2 rounded-sm transition-colors duration-200 ease-in-out ${rememberMe ? 'bg-primary-500 border-primary-500' : 'border-gray-300 dark:border-gray-600'}`}>
                                                <Check className={`w-4 h-4 text-white transition-opacity duration-200 ease-in-out ${rememberMe ? 'opacity-100' : 'opacity-0'}`} />
                                            </div>
                                        </div>
                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                                            Remember me
                                        </span>
                                    </label>
                                </div>

                                <div className="text-sm">
                                    <a href="/forgot-password" className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300">
                                        Forgot password?
                                    </a>
                                </div>
                            </div>

                            {/* Error message */}
                            {(error || networkError) && (
                                <div className="rounded-md bg-red-50 p-4 border border-red-300" role="alert">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-red-800">{error || networkError}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Pending approval message */}
                            {pendingApproval && (
                                <div className="rounded-md bg-yellow-50 p-4 border border-yellow-300" role="alert">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-yellow-800">Your account is pending approval. Please wait for an admin to approve your role request.</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Debug section for MFA issues (development only) */}
                            {(process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') && (mfaRequired || mfaSetupRequired) && (
                                <div className="rounded-md bg-blue-50 p-4 border border-blue-300 mb-4">
                                    <div className="text-center">
                                        <p className="text-sm text-blue-800 mb-2">
                                            Debug: MFA State Detected (Dev Mode Only)
                                        </p>
                                        <p className="text-xs text-blue-600 mb-3">
                                            MFA Setup Required: {mfaSetupRequired ? 'Yes' : 'No'} | MFA Required: {mfaRequired ? 'Yes' : 'No'}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                console.log('[LOGIN] Clearing stuck MFA state');
                                                clearMFAState();
                                            }}
                                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            Clear MFA State & Logout
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Submit button */}
                            <div>
                                <button
                                    type="submit"
                                    className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm dark:shadow-gray-900/20 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 ease-in-out"
                                    disabled={loading}
                                    onClick={(e) => {
                                        console.log('[LOGIN] Submit button clicked!', e);
                                        console.log('[LOGIN] Button event details:', {
                                            type: e.type,
                                            target: e.target,
                                            currentTarget: e.currentTarget
                                        });
                                    }}
                                >
                                    {loading ? 'Signing in...' : 'Sign in'}
                                    {!loading && <ChevronRight className="ml-2 h-5 w-5" />}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </motion.div>
            
            {/* MFA Setup Modal */}
            {mfaSetupRequired && (
                <MFASetup
                    onSetupComplete={handleMFASetupComplete}
                    onCancel={null} // Don't allow cancel when MFA is required
                    userEmail={user?.email}
                    isRequired={true} // MFA is always required when shown on login page
                />
            )}
        </div>
    );
}

export default Login;